<template>
  <div class="w-full h-full relative min-h-0 min-w-0">
    <div class="w-full h-full px-4 overflow-hidden" :class="props.isHide ? 'blur-sm' : ''">
      <VChart autoresize :option="chartOptions" style="width: 100%; height: 100%; z-index: 1;" />
      <div v-if="props.showBottomTitle" class="justify-align-center text-txopacity -mt-8">
        {{ props.dates[0] }} - {{ props.dates[props.dates.length - 1] }}
      </div>
    </div>
    <div v-if="props.isHide" class="flex items-center justify-center absolute top-0 left-0 w-full h-full z-[2]">
    </div>
    <div v-if="props.isHide" class="text-txopacity text-sm absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[3]">
      Нет данных
    </div>
  </div>
</template>

<script setup lang="ts">
  import * as echarts from "echarts";

  const props = withDefaults(
    defineProps<{
      dates: string[];
      series: number[];
      lineColor?: string;
      showBottomTitle?: boolean;
      param?: number | string;
      isHide?: boolean;
    }>(),
    {
      dates: () => [],
      series: () => [],
      lineColor: `#3176FF`,
      showBottomTitle: true,
      isHide: false,
    }
  );

  const chartOptions = ref<any>({
    tooltip: {
      trigger: "axis",
      formatter: function (params: any) {
        params = params[0];
        return `<div class="flex flex-col gap-1 rounded-xl p-1">
          <div class="text-txopacity text-[0.6rem]">${params.name}</div>
          <div class="text-txprimary text-xs">${params.value.toLocaleString()}</div>
        </div>`;
      },
      axisPointer: {
        animation: false,
      },
      padding: 2,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: props.dates,
      axisLine: {
        lineStyle: {
          color: "#3A3D4499",
        },
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        margin: 20,
      },
    },
    yAxis: {
      type: "value",
      boundaryGap: false,
      scale: true,
      min: (extent: { min: number; max: number }) => {
        const paddingRatio = isNaN(Number(props.param)) ? 0.45 : Number(props.param);
        const range = extent.max - extent.min;
        const safeRange = range === 0 ? Math.max(Math.abs(extent.max) || 1, 1) : range;
        const pad = safeRange * paddingRatio;
        return Math.max(0, Math.floor(extent.min - pad / 2));
      },
      max: (extent: { min: number; max: number }) => {
        const paddingRatio = isNaN(Number(props.param)) ? 0.45 : Number(props.param);
        const range = extent.max - extent.min;
        const safeRange = range === 0 ? Math.max(Math.abs(extent.max) || 1, 1) : range;
        const pad = safeRange * paddingRatio;
        return Math.ceil(extent.max + pad / 2);
      },
      splitLine: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: "#3A3D4499",
        },
        show: false,
      },
    },
    grid: {
      top: "4%",
      left: "1%",
      right: "4%",
      bottom: "8%",
      containLabel: true,
    },
    series: [
      {
        data: props.series,
        type: "line",
        showSymbol: false,
        smooth: true,
        lineStyle: {
          color: props.lineColor ?? "#3176FF",
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: `${props.lineColor}3D`,
            },
            {
              offset: 1,
              color: `${props.lineColor}00`,
            },
          ]),
        },
      },
    ],
  });

  watch(
    () => props.series,
    (newSeries) => {
      chartOptions.value.series[0].data = newSeries;
    }
  );

  watch(
    () => props.dates,
    (newDates) => {
      chartOptions.value.xAxis.data = newDates;
    }
  );
</script>

<style lang="scss"></style>
